"use client";

import { useCallback, useEffect, useState } from "react";
import {
  COOKIE_CONSENT_UPDATED_EVENT,
  CookieConsent,
  DEFAULT_COOKIE_CONSENT,
  getCookieConsent,
  setCookieConsent,
} from "@/lib/consent";

type ConsentSetter = (next: CookieConsent | ((prev: CookieConsent) => CookieConsent)) => void;

export function useConsent(): { consent: CookieConsent; setConsent: ConsentSetter } {
  const [consent, setConsentState] = useState<CookieConsent>(() =>
    typeof window === "undefined" ? DEFAULT_COOKIE_CONSENT : getCookieConsent(),
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handleChange = () => setConsentState(getCookieConsent());
    window.addEventListener(COOKIE_CONSENT_UPDATED_EVENT, handleChange);
    window.addEventListener("storage", handleChange);
    return () => {
      window.removeEventListener(COOKIE_CONSENT_UPDATED_EVENT, handleChange);
      window.removeEventListener("storage", handleChange);
    };
  }, []);

  const setConsent: ConsentSetter = useCallback((next) => {
    setConsentState((prev) => {
      const resolved = typeof next === "function" ? (next as (current: CookieConsent) => CookieConsent)(prev) : next;
      return setCookieConsent(resolved);
    });
  }, []);

  return { consent, setConsent };
}
